import posthog from './posthogClient';

/**
 * Client-side A/B assignment.
 *
 * PostHog resolves flags over the network after init, so letting it decide means
 * either a blank gate or a visible swap on first paint. This assigns the variant
 * synchronously instead and reports the choice to PostHog afterwards, so the page
 * renders immediately.
 *
 * The assignment is stored in localStorage so a visitor keeps the same variant
 * across page loads — without that, every load would re-roll and PostHog would
 * drop them from the analysis for being exposed to both variants.
 *
 * When PostHog flags happen to already be cached, its answer wins. That keeps the
 * flag working as a kill switch for new visitors: switch it off and nobody new is
 * enrolled. Visitors already assigned keep their variant, which is what you want
 * mid-experiment.
 */

const STORAGE_PREFIX = 'tb2_ab_';

export const CONTROL = 'control';
export const TEST = 'test';

const storageKey = (flagKey) => `${STORAGE_PREFIX}${flagKey}`;
const exposureKey = (flagKey) => `${STORAGE_PREFIX}${flagKey}_exposed`;

const NOT_ENROLLED = { enrolled: false, variant: null };

function readStored(flagKey) {
    try {
        const raw = window.localStorage.getItem(storageKey(flagKey));
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (typeof parsed?.enrolled !== 'boolean') return null;
        if (parsed.enrolled && parsed.variant !== CONTROL && parsed.variant !== TEST) return null;

        return { enrolled: parsed.enrolled, variant: parsed.enrolled ? parsed.variant : null };
    } catch {
        return null;
    }
}

function writeStored(flagKey, assignment) {
    try {
        window.localStorage.setItem(storageKey(flagKey), JSON.stringify(assignment));
    } catch {
        // Private browsing or a full quota — the visitor still gets a variant for
        // this page load, they just may be re-rolled on the next one.
    }
}

// hasLoadedFlags is a getter in posthog-js 1.310 and was a method in older
// releases. Read it defensively: nothing here may throw into a render path.
function flagsAreLoaded() {
    try {
        if (!posthog?.__loaded) return false;

        const flags = posthog.featureFlags;
        if (!flags) return false;

        const loaded = flags.hasLoadedFlags;
        return Boolean(typeof loaded === 'function' ? loaded.call(flags) : loaded);
    } catch {
        return false;
    }
}

function assignmentFromFlag(flagKey) {
    try {
        // send_event: false — reading the flag would otherwise emit its own
        // $feature_flag_called, duplicating the one trackExposure sends. Exposure
        // is reported from exactly one place.
        const value = posthog.getFeatureFlag(flagKey, { send_event: false });
        if (value === TEST || value === CONTROL) {
            return { enrolled: true, variant: value };
        }
        // Falsy means the flag is off or this visitor is outside its rollout.
        return NOT_ENROLLED;
    } catch {
        return null;
    }
}

function assignmentFromCoinFlip(enrolledShare, testShare) {
    if (Math.random() >= enrolledShare) return NOT_ENROLLED;

    return {
        enrolled: true,
        variant: Math.random() < testShare ? TEST : CONTROL,
    };
}

/**
 * Resolve the variant for this visitor. Synchronous — safe to call during render.
 *
 * @param {string} flagKey       PostHog flag key, also the localStorage key suffix
 * @param {number} enrolledShare 0-1, share of visitors entering the experiment
 * @param {number} testShare     0-1, share of *enrolled* visitors seeing the variant
 * @returns {{ enrolled: boolean, variant: 'control'|'test'|null }}
 */
export function resolveVariant({ flagKey, enrolledShare, testShare }) {
    const stored = readStored(flagKey);
    if (stored) return stored;

    const assignment =
        (flagsAreLoaded() ? assignmentFromFlag(flagKey) : null) ??
        assignmentFromCoinFlip(enrolledShare, testShare);

    writeStored(flagKey, assignment);
    return assignment;
}

/**
 * Read an existing assignment without creating one.
 *
 * resolveVariant() assigns and persists on its first call. That is right at the
 * point of enrollment and wrong everywhere else: a page that merely needs to
 * know the variant would otherwise enroll visitors who never met the entry
 * condition. Read-only callers use this and see "not enrolled" until the
 * enrolling call site has run.
 *
 * @param {string} flagKey
 * @returns {{ enrolled: boolean, variant: 'control'|'test'|null }}
 */
export function peekVariant(flagKey) {
    return readStored(flagKey) ?? NOT_ENROLLED;
}

/**
 * Report the assignment to PostHog, using the same event and payload its own SDK
 * emits, so the experiment counts it as an exposure. Fires once per session and
 * only for enrolled visitors.
 */
export function trackExposure(flagKey, variant) {
    if (!variant) return;

    try {
        if (!posthog?.__loaded) return;

        if (window.sessionStorage.getItem(exposureKey(flagKey))) return;
        window.sessionStorage.setItem(exposureKey(flagKey), '1');
    } catch {
        // sessionStorage unavailable — fall through and send it. A duplicate
        // exposure is harmless for person-level analysis; a missing one is not.
    }

    try {
        // Tags every subsequent event with the variant, so downstream metrics can
        // be attributed and filtered the same way PostHog-assigned flags are.
        posthog.register({ [`$feature/${flagKey}`]: variant });

        posthog.capture('$feature_flag_called', {
            $feature_flag: flagKey,
            $feature_flag_response: variant,
        });
    } catch {
        // Analytics must never break the page.
    }
}
